import { makeStyles } from '@mui/styles';
import Button from 'apollo-react/components/Button';
import Table from 'apollo-react/components/Table';
import Plus from 'apollo-react-icons/Plus';
import _ from 'lodash';
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { columns } from './columns';
import ExpandableComponent from './ExpandableComponent';
import { useTableLastRowVisibilityEffect } from '../../../custom-hooks';
import { useTranslation } from '../../../custom-hooks/useTranslation';
import { equalityFn } from '../../../form-designer/utils/form-manager.utils';
import { addControl } from '../../actions/report-designer.actions';
import { REPORT_CONTROL_TYPE } from '../../constants/report-designer.constants';
import {
  getHasValidationErrorById,
  getReportConditionsTableRows,
  getSelectedControlId,
} from '../../selectors/report-designer.selectors';
import { getChildControlType } from '../../utils/report-designer.utils';

const useStyles = makeStyles({
  buttonContainer: {
    marginBottom: 8,
    textAlign: 'right',
  },
  selected: {
    '& td': {
      backgroundColor: '#0768fd !important',
    },
  },
  error: {
    border: 'solid 2px red !important',
    borderRadius: 5,
    '&:hover': {
      border: 'solid 2px red !important',
    },
  },
});

const SSReportConditionsTable = ({ controlType, id, childrenNodes }) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const translateString = useTranslation();

  const tableRef = useRef(null);
  const selectedControlId = useSelector(getSelectedControlId);
  const rows = useSelector((state) => getReportConditionsTableRows(state, { id }), equalityFn);
  const hasValidationError = useSelector((state) => getHasValidationErrorById(state, { id }));

  const [expandedRowId, setExpandedRowId] = useState(null);

  const isExpandableRowsTable = controlType === REPORT_CONTROL_TYPE.FILTER_CONDITION_CONTAINER;

  useEffect(() => {
    if (selectedControlId !== expandedRowId) {
      setExpandedRowId(null);
    }
  }, [selectedControlId, expandedRowId]);

  useTableLastRowVisibilityEffect({
    tableRef,
    rows,
    isExpandableRowsTable,
  });

  const handleAddRowClick = () => {
    dispatch(
      addControl({
        prevControlId: _.last(childrenNodes),
        parentId: id,
        controlType: getChildControlType(controlType),
      })
    );
  };

  return (
    <>
      <div className={classes.buttonContainer}>
        <Button color="primary" variant="secondary" onClick={handleAddRowClick}>
          <Plus fontSize="small" style={{ marginRight: 5 }} />
          {translateString('Add')}
        </Button>
      </div>
      <div ref={tableRef}>
        <Table
          columns={columns[controlType]}
          className={hasValidationError ? classes.error : null}
          rows={rows.map((row) => ({
            ...row,
            expanded: row.controlId === expandedRowId,
            className: row.controlId === selectedControlId ? classes.selected : null,
            selected: row.controlId === selectedControlId,
            expandedRowId,
            setExpandedRowId,
          }))}
          rowId="controlId"
          hidePagination
          ExpandableComponent={isExpandableRowsTable ? ExpandableComponent : undefined}
        />
      </div>
    </>
  );
};

export default SSReportConditionsTable;
